<template>
  <Layout :hide-route-header="showOnboarding">
    <div v-if="isAgent">
      <h1>Welcome!</h1>
    </div>
    <div v-if="isNotAgent">
      <div v-if="showQuickstart">
        <DashboardQuickstart />
      </div>
      <div v-else>
        <div class="grid grid-cols-1">
          <div class="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-1 lg:grid-cols-2">
            <card>
              <div class="flex justify-between">
                <h2 id="nurture-title" class="text-lg font-medium text-gray-900">Today's Meetings</h2>
                <div>
                  <router-link
                    to="/contacts/calendar"
                    class="cursor-pointer inline-flex items-center rounded-md px-2.5 py-0.5 text-sm font-medium text-indigo-800 bg-indigo-100"
                  >
                    Calendar
                  </router-link>
                </div>
              </div>
              <div v-if="todaysMeetings.length == 0" class="text-sm whitespace-nowrap text-gray-500 mt-12">
                No meetings scheduled for today
              </div>
              <div
                v-for="meeting in todaysMeetings"
                :key="meeting.id"
                class="mt-4 relative flex space-x-3 items-center"
              >
                <div class="min-w-0 flex-1 pt-1.5 xl:flex justify-start xl:space-x-4 items-center">
                  <div>
                    <p class="text-sm text-gray-500">
                      <router-link :to="meetingUrl(meeting)" class="font-medium text-indigo-600 hover:text-indigo-500">
                        {{ meeting.name }}
                      </router-link>
                    </p>
                  </div>
                  <span class="inline-block xl:ml-2 text-sm whitespace-nowrap text-gray-500">
                    {{ formatDate(meeting.start_time, 'h:mm a') }} -
                    {{ formatDate(meeting.end_time, 'h:mm a') }}
                  </span>
                </div>
                <router-link :to="meetingUrl(meeting)" class="text-xs border border-gray-200 rounded p-1">{{
                  eventAction(meeting)
                }}</router-link>
              </div>
            </card>

            <card>
              <div class="flex justify-between">
                <h2 id="nurture-title" class="text-lg font-medium text-gray-900">Tomorrow's Meetings</h2>
                <div>
                  <router-link
                    to="/contacts/calendar"
                    class="cursor-pointer inline-flex items-center rounded-md px-2.5 py-0.5 text-sm font-medium text-indigo-800 bg-indigo-100"
                  >
                    Calendar
                  </router-link>
                </div>
              </div>
              <div v-if="tomorrowsMeetings.length == 0" class="text-sm whitespace-nowrap text-gray-500 mt-12">
                No meetings scheduled for tomorrow
              </div>
              <div
                v-for="meeting in tomorrowsMeetings"
                :key="meeting.id"
                class="mt-4 relative flex space-x-3 items-center"
              >
                <div class="min-w-0 flex-1 pt-1.5 xl:flex justify-start xl:space-x-4 items-center">
                  <div>
                    <p class="text-sm text-gray-500">
                      <router-link :to="meetingUrl(meeting)" class="font-medium text-indigo-600 hover:text-indigo-500">
                        {{ meeting.name }}
                      </router-link>
                    </p>
                  </div>
                  <span class="inline-block xl:ml-2 text-sm whitespace-nowrap text-gray-500">
                    {{ formatDate(meeting.start_time, 'h:mm a') }} -
                    {{ formatDate(meeting.end_time, 'h:mm a') }}
                  </span>
                </div>
                <router-link :to="meetingUrl(meeting)" class="text-xs border border-gray-200 rounded p-1">{{
                  eventAction(meeting)
                }}</router-link>
              </div>
            </card>
          </div>
        </div>
        <div class="mt-10">
          <div class="pt-5 sm:pt-6 flex justify-between">
            <div class="flex">
              <h2 id="nurture-title" class="text-lg font-medium text-gray-900 mr-2">Recruiting Performance</h2>
              <LoadingSpinner />
            </div>
            <div class="flex items-center">
              <div
                data-track-intent="custom-reporting-date"
                class="hidden md:block cursor-pointer relative rounded-md px-2.5 py-0.5 text-sm font-medium text-indigo-800"
                :class="metricsTimeline == 'Custom' ? 'bg-indigo-100' : ''"
                @click="openCustomTimeframeMenu"
              >
                Custom

                <span
                  v-if="customReportingTimeframe.start && customReportingTimeframe.end"
                  class="text-indigo-800 font-baseline"
                  >: {{ customReportingTimeframe.start }} - {{ customReportingTimeframe.end }}</span
                >

                <div
                  v-if="showCustomDatePicker"
                  class="absolute block p-4 top-full right-0 mt-4 w-screen max-w-sm bg-white rounded border border-slate-200"
                  @click.stop="() => {}"
                >
                  <input
                    ref="flatpickrInput"
                    type="text"
                    placeholder="Select reporting timeframe..."
                    data-id="range"
                    readonly="readonly"
                    class="w-full text-sm text-gray-600 border border-slate-200 rounded"
                  />
                  <button
                    data-track="custom-reporting-date"
                    class="inline-block mt-4 px-3 py-2 rounded bg-[#fde68a] font-semibold text-sm text-slate-800"
                    :class="{
                      'opacity-30 not-allowed': !customReportingTimeframe.start || !customReportingTimeframe.end,
                    }"
                    @click="getDashboardDetails"
                  >
                    Update timeframe
                  </button>
                </div>
              </div>
              <span
                class="cursor-pointer inline-flex items-center rounded-md px-2.5 py-0.5 text-sm font-medium text-indigo-800"
                :class="metricsTimeline == 'All Time' ? 'bg-indigo-100' : ''"
                @click="
                  metricsTimeline = 'All Time';
                  getDashboardDetails();
                "
              >
                All Time
              </span>
              <span
                class="cursor-pointer inline-flex items-center rounded-md px-2.5 py-0.5 text-sm font-medium text-indigo-800"
                :class="metricsTimeline == 'This Year' ? 'bg-indigo-100' : ''"
                @click="
                  metricsTimeline = 'This Year';
                  getDashboardDetails();
                "
              >
                This Year
              </span>
              <span
                class="cursor-pointer inline-flex items-center rounded-md px-2.5 py-0.5 text-sm font-medium text-indigo-800"
                :class="metricsTimeline == 'Last Month' ? 'bg-indigo-100' : ''"
                @click="
                  metricsTimeline = 'Last Month';
                  getDashboardDetails();
                "
              >
                Last Month
              </span>
              <span
                class="cursor-pointer inline-flex items-center rounded-md px-2.5 py-0.5 text-sm font-medium text-indigo-800"
                :class="metricsTimeline == 'This Month' ? 'bg-indigo-100' : ''"
                @click="
                  metricsTimeline = 'This Month';
                  getDashboardDetails();
                "
              >
                This Month
              </span>
            </div>
          </div>
        </div>
        <div
          v-if="$store.state.general.currentUserAccountDetails.role != 'Agent'"
          class="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 auto-rows-max"
        >
          <router-link to="/contacts?sort=created&order=desc&stage=All">
            <card>
              <div class="flex items-center">
                <div class="ml-5 w-0 flex-1">
                  <dl>
                    <dt class="text-sm leading-5 font-medium text-gray-500 truncate">Candidates Sourced</dt>
                    <dd class="flex items-baseline mt-8 justify-between">
                      <div class="text-2xl leading-8 font-semibold text-gray-900">
                        {{ candidatesSourced }}
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </card>
          </router-link>
          <router-link to="/contacts?sort=created&order=desc&stage=Phone%20Screen">
            <card>
              <div class="flex items-center">
                <div class="ml-5 w-0 flex-1">
                  <dl>
                    <dt class="text-sm leading-5 font-medium text-gray-500 truncate">Phone Screened</dt>
                    <dd class="flex items-center mt-8 justify-between">
                      <div class="text-2xl leading-8 font-semibold text-gray-900">
                        {{ candidatesPhoneScreened }}
                      </div>

                      <div
                        class="rounded-full px-2 py-1 text-xs font-semibold"
                        :class="{
                          'bg-green-200 text-green-500': phoneScreenRate >= 0.75,
                          'bg-yellow-200 text-yellow-500': phoneScreenRate < 0.75 && phoneScreenRate >= 0.6,
                          'bg-red-200 text-red-500': phoneScreenRate < 0.6,
                        }"
                      >
                        {{ phoneScreenRate ? (phoneScreenRate * 100).toFixed(2) : 0 }}%
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </card>
          </router-link>
          <router-link to="/contacts?sort=created&order=desc&stage=Interview">
            <card>
              <div class="flex items-center">
                <div class="ml-5 w-0 flex-1">
                  <dl>
                    <dt class="text-sm leading-5 font-medium text-gray-500 truncate">Interviewed</dt>
                    <dd class="flex items-center mt-8 justify-between">
                      <div class="text-2xl leading-8 font-semibold text-gray-900">
                        {{ candidatesInterviewed }}
                      </div>
                      <div
                        class="rounded-full px-2 py-1 text-xs font-semibold"
                        :class="{
                          'bg-green-200 text-green-500': interviewRate >= 0.35,
                          'bg-yellow-200 text-yellow-500': interviewRate < 0.35 && interviewRate >= 0.25,
                          'bg-red-200 text-red-500': interviewRate < 0.25,
                        }"
                      >
                        {{ interviewRate ? (interviewRate * 100).toFixed(2) : 0 }}%
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </card>
          </router-link>
          <router-link to="/contacts?sort=created&order=desc&stage=Hired">
            <card>
              <div class="flex items-center">
                <div class="ml-5 w-0 flex-1">
                  <dl>
                    <dt class="text-sm leading-5 font-medium text-gray-500 truncate">Hired</dt>
                    <dd class="flex items-center mt-8 justify-between">
                      <div class="text-2xl leading-8 font-semibold text-gray-900">
                        {{ candidatesHired }}
                      </div>
                      <div
                        class="rounded-full px-2 py-1 text-xs font-semibold"
                        :class="{
                          'bg-green-200 text-green-500': hireRate >= 0.1,
                          'bg-yellow-200 text-yellow-500': hireRate < 0.1 && hireRate >= 0.08,
                          'bg-red-200 text-red-500': hireRate < 0.08,
                        }"
                      >
                        {{ hireRate ? (hireRate * 100).toFixed(2) : 0 }}%
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </card>
          </router-link>
        </div>
        <div class="relative mt-4">
          <div class="absolute inset-0 flex items-center" aria-hidden="true">
            <div class="w-full border-t border-gray-300"></div>
          </div>
          <div class="relative flex justify-center">
            <button
              type="button"
              class="inline-flex items-center gap-x-1.5 rounded-full bg-white px-3 py-1.5 text-sm text-gray-500 hover:text-gray-800"
              @click="showUnlicensed = !showUnlicensed"
            >
              <svg
                class="-ml-1 -mr-0.5 h-5 w-5 text-gray-200"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z"
                />
              </svg>
              {{ showUnlicensed ? 'Hide' : 'Show' }} additional metrics
            </button>
          </div>
        </div>

        <transition
          enter-active-class="transition-opacity ease-linear duration-300"
          leave-active-class="transition-opacity ease-linear duration-300"
          enter-class="opacity-0"
          enter-to-class="opacity-100"
          leave-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <div
            v-if="$store.state.general.currentUserAccountDetails.role != 'Agent' && showUnlicensed"
            class="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 auto-rows-max"
          >
            <router-link to="/contacts?sort=created&order=desc&stage=Unlicensed">
              <card v-if="showUnlicensed">
                <div class="flex items-center">
                  <div class="ml-5 w-0 flex-1">
                    <dl>
                      <dt class="text-sm leading-5 font-medium text-gray-500 truncate">
                        Unlicensed Candidates Sourced
                      </dt>
                      <dd class="flex items-baseline mt-8">
                        <div class="text-2xl leading-8 font-semibold text-gray-900">
                          {{ candidatesUnlicensed }}
                        </div>
                      </dd>
                    </dl>
                  </div>
                </div>
              </card>
            </router-link>
            <router-link to="/contacts?sort=created&order=desc&stage=Unlicensed">
              <card>
                <div class="flex items-center">
                  <div class="ml-5 w-0 flex-1">
                    <dl>
                      <dt class="text-sm leading-5 font-medium text-gray-500 truncate">
                        Unlicensed Candidates Coursework Started Sourced
                      </dt>
                      <dd class="flex items-baseline mt-8">
                        <div class="text-2xl leading-8 font-semibold text-gray-900">
                          {{ candidatesUnlicensedCourseworkStarted }}
                        </div>
                      </dd>
                    </dl>
                  </div>
                </div>
              </card>
            </router-link>
          </div>
        </transition>

        <div class="mt-10">
          <div class="pt-5 sm:pt-6 flex justify-between">
            <div class="flex">
              <h2 id="nurture-title" class="text-lg font-medium text-gray-900 mr-2">Team Performance</h2>
              <LoadingSpinner />
            </div>
            <div class="dropdown dropdown-bottom dropdown-end">
              <span
                v-if="pipelineReviewerFilter == 'All'"
                tabindex="0"
                role="button"
                class="cursor-pointer inline-flex items-center rounded-md px-2.5 py-0.5 text-sm font-medium text-indigo-800 bg-indigo-100"
              >
                All Pipeline Reviewers
              </span>
              <span
                v-else
                tabindex="0"
                role="button"
                class="cursor-pointer inline-flex items-center rounded-md px-2.5 py-0.5 text-sm font-medium text-indigo-800 bg-indigo-100"
              >
                Pipeline Reviewer: {{ pipelineReviewerFilter }}
              </span>
              <div
                tabindex="0"
                class="dropdown-content z-[1] card card-compact w-64 p-2 border border-gray-200 bg-white text-primary-content"
              >
                <div class="card-body">
                  <label class="form-control w-full max-w-xs">
                    <div class="label mt-0 pt-0">
                      <span class="label-text text-gray-600">Pipeline Reviewer</span>
                    </div>
                    <select
                      v-model="pipelineReviewerFilter"
                      class="select select-bordered text-gray-800"
                      @change="getDashboardDetails"
                    >
                      <option value="All">All</option>
                      <option v-for="reviewer of eligiblePipelineReviewers" :key="reviewer" :value="reviewer">
                        {{ reviewer }}
                      </option>
                    </select>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="mt-5 grid sm:grid-cols-3 gap-x-2 gap-y-8">
          <div class="sm:col-span-1">
            <card>
              <div class="flex items-center">
                <div class="ml-5 w-0 flex-1">
                  <dl>
                    <dt class="text-sm leading-5 font-medium text-gray-500 truncate">Pipeline Reviews Completed</dt>
                    <dd class="flex items-center mt-8 justify-between">
                      <div v-if="pipelineReviewsCompleted == 0" class="text-sm text-gray-500 max-w-xl mx-auto">
                        No Pipeline Reviews yet. Start automating your Pipeline Reviews today.
                        <router-link
                          class="font-medium text-indigo-600 hover:text-indigo-500"
                          to="/settings/workflows/team"
                          >Enable Pipeline Reviews</router-link
                        >
                      </div>
                      <div v-else class="text-2xl leading-8 font-semibold text-gray-900">
                        {{ pipelineReviewsCompleted }}
                      </div>
                      <div
                        v-if="pipelineReviewsCompleted != 0"
                        class="rounded-full px-2 py-1 text-xs font-semibold bg-gray-200 text-gray-500"
                      >
                        {{ pipelineReviewCompletionRate ? (pipelineReviewCompletionRate * 100).toFixed(2) : 0 }}% of
                        team
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </card>
          </div>
          <div class="sm:col-span-2 flex gap-2 relative justify-center flex-wrap">
            <div class="h-32 w-32 rounded-lg border flex items-center justify-center text-red-600 relative">
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 256 256">
                <path
                  fill="currentColor"
                  d="M128 26a102 102 0 1 0 102 102A102.12 102.12 0 0 0 128 26m0 192a90 90 0 1 1 90-90a90.1 90.1 0 0 1-90 90M82 108a10 10 0 1 1 10 10a10 10 0 0 1-10-10m92 0a10 10 0 1 1-10-10a10 10 0 0 1 10 10m-.81 65a6 6 0 0 1-10.38 6c-7.84-13.54-20.2-21-34.81-21s-27 7.46-34.81 21a6 6 0 0 1-5.2 3a5.9 5.9 0 0 1-3-.81a6 6 0 0 1-2.18-8.19c9.92-17.16 26.39-27 45.19-27s35.27 9.84 45.19 27"
                />
              </svg>
              <!-- <div class="text-md leading-8 font-semibold text-gray-900 absolute top-0 left-2">{{pipelineReviews["1"]}}</div> -->
              <div
                class="absolute top-1 right-2 rounded-full px-2 py-1 text-xs font-semibold bg-gray-200 text-gray-500"
              >
                {{
                  pipelineReviewsCompleted && pipelineReviews['1']
                    ? ((pipelineReviews['1'] / pipelineReviewsCompleted) * 100).toFixed(2)
                    : 0
                }}%
              </div>
            </div>
            <div class="h-32 w-32 rounded-lg border flex items-center justify-center text-red-300 relative">
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 256 256">
                <path
                  fill="currentColor"
                  d="M128 26a102 102 0 1 0 102 102A102.12 102.12 0 0 0 128 26m0 192a90 90 0 1 1 90-90a90.1 90.1 0 0 1-90 90m46-58a6 6 0 0 1-6 6H88a6 6 0 0 1 0-12h80a6 6 0 0 1 6 6m-92-52a10 10 0 1 1 10 10a10 10 0 0 1-10-10m92 0a10 10 0 1 1-10-10a10 10 0 0 1 10 10"
                />
              </svg>
              <!-- <div class="text-md leading-8 font-semibold text-gray-900 absolute top-1 right-2">{{pipelineReviews["2"]}}</div> -->
              <div
                class="absolute top-1 right-2 rounded-full px-2 py-1 text-xs font-semibold bg-gray-200 text-gray-500"
              >
                {{
                  pipelineReviewsCompleted && pipelineReviews['2']
                    ? ((pipelineReviews['2'] / pipelineReviewsCompleted) * 100).toFixed(2)
                    : 0
                }}%
              </div>
            </div>
            <div class="h-32 w-32 rounded-lg border flex items-center justify-center text-yellow-400 relative">
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 256 256">
                <path
                  fill="currentColor"
                  d="M128 26a102 102 0 1 0 102 102A102.12 102.12 0 0 0 128 26m0 192a90 90 0 1 1 90-90a90.1 90.1 0 0 1-90 90m46-58a6 6 0 0 1-6 6H88a6 6 0 0 1 0-12h80a6 6 0 0 1 6 6m-92-52a10 10 0 1 1 10 10a10 10 0 0 1-10-10m92 0a10 10 0 1 1-10-10a10 10 0 0 1 10 10"
                />
              </svg>
              <!-- <div class="text-md leading-8 font-semibold text-gray-900 absolute top-1 right-2">{{pipelineReviews["3"]}}</div> -->
              <div
                class="absolute top-1 right-2 rounded-full px-2 py-1 text-xs font-semibold bg-gray-200 text-gray-500"
              >
                {{
                  pipelineReviewsCompleted && pipelineReviews['3']
                    ? ((pipelineReviews['3'] / pipelineReviewsCompleted) * 100).toFixed(2)
                    : 0
                }}%
              </div>
            </div>
            <div class="h-32 w-32 rounded-lg border flex items-center justify-center text-green-300 relative">
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 256 256">
                <path
                  fill="currentColor"
                  d="M128 26a102 102 0 1 0 102 102A102.12 102.12 0 0 0 128 26m0 192a90 90 0 1 1 90-90a90.1 90.1 0 0 1-90 90m46-58a6 6 0 0 1-6 6H88a6 6 0 0 1 0-12h80a6 6 0 0 1 6 6m-92-52a10 10 0 1 1 10 10a10 10 0 0 1-10-10m92 0a10 10 0 1 1-10-10a10 10 0 0 1 10 10"
                />
              </svg>
              <!-- <div class="text-md leading-8 font-semibold text-gray-900 absolute top-1 right-2">{{pipelineReviews["4"]}}</div> -->
              <div
                class="absolute top-1 right-2 rounded-full px-2 py-1 text-xs font-semibold bg-gray-200 text-gray-500"
              >
                {{
                  pipelineReviewsCompleted && pipelineReviews['4']
                    ? ((pipelineReviews['4'] / pipelineReviewsCompleted) * 100).toFixed(2)
                    : 0
                }}%
              </div>
            </div>
            <div class="h-32 w-32 rounded border flex items-center justify-center text-green-600 relative">
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 256 256">
                <path
                  fill="currentColor"
                  d="M173.19 155c-9.92 17.16-26.39 27-45.19 27s-35.27-9.84-45.19-27a6 6 0 0 1 10.38-6c7.84 13.54 20.2 21 34.81 21s27-7.46 34.81-21a6 6 0 1 1 10.38 6M230 128A102 102 0 1 1 128 26a102.12 102.12 0 0 1 102 102m-12 0a90 90 0 1 0-90 90a90.1 90.1 0 0 0 90-90M92 118a10 10 0 1 0-10-10a10 10 0 0 0 10 10m72-20a10 10 0 1 0 10 10a10 10 0 0 0-10-10"
                />
              </svg>
              <!-- <div class="text-md leading-8 font-semibold text-gray-900 absolute top-1 right-2">{{pipelineReviews["5"]}}</div> -->
              <div
                class="absolute top-1 right-2 rounded-full px-2 py-1 text-xs font-semibold bg-gray-200 text-gray-500"
              >
                {{
                  pipelineReviewsCompleted && pipelineReviews['5']
                    ? ((pipelineReviews['5'] / pipelineReviewsCompleted) * 100).toFixed(2)
                    : 0
                }}%
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from '../components/Layout/index.vue';
import Card from '../components/Card/index.vue';
import flatpickr from 'flatpickr';
import LoadingSpinner from '../components/shared/LoadingSpinner.vue';
import DashboardQuickstart from '../components/dashboard_quickstart/DashboardQuickstart.vue';
import DateUtility from '../utilities/dates';

export default {
  components: {
    Layout,
    Card,
    LoadingSpinner,
    DashboardQuickstart,
  },
  data() {
    return {
      showUnlicensed: false,
      showCustomDatePicker: false,
      customReportingTimeframe: {
        start: null,
        end: null,
      },
      candidatesSourced: 0,
      activeAgents: 0,
      hireRate: 0,
      phoneScreenRate: 0,
      interviewRate: 0,
      candidatesPhoneScreened: 0,
      candidatesInterviewed: 0,
      candidatesHired: 0,
      candidatesUnlicensed: 0,
      candidatesUnlicensedCourseworkStarted: 0,
      pipelineReviewsCompleted: 0,
      pipelineReviewCompletionRate: 0,
      todaysMeetings: [],
      tomorrowsMeetings: [],
      pipelineReviews: {},
      referralTimeline: 'This Month',
      metricsTimeline: 'This Month',
      eligiblePipelineReviewers: [],
      pipelineReviewerFilter: 'All',
    };
  },
  computed: {
    isAgent() {
      return this.$store.state.general.currentUserAccountDetails.role === 'Agent';
    },
    isNotAgent() {
      const nonAgents = ['Admin', 'Contributor'];
      return nonAgents.includes(this.$store.state.general.currentUserAccountDetails.role);
    },
    showOnboarding() {
      return this.$store.state.general.currentUserAccountDetails?.featureFlags?.dashboard_onboarding_flow;
    },
    showQuickstart() {
      return this.$store.state.general.currentUserAccountDetails?.showQuickstart;
    },
  },
  watch: {
    date() {
      this.getDashboardDetails();
    },
  },
  async mounted() {
    await this.getDashboardDetails();

    if (this.isAgent) {
      await this.$router.push('/agents/onboarding');
    }
  },
  async updated() {
    if (this.isAgent) {
      await this.$router.push('/agents/onboarding');
    }
  },
  methods: {
    ...DateUtility,
    async getDashboardDetails() {
      const self = this;
      let url = `/api/dashboard?month_constraint=${
        self.referralTimeline == 'This Month' ? '0' : '30'
      }&metrics_time_constraint=${self.metricsTimeline}&pipeline_reviewer=${self.pipelineReviewerFilter}`;
      let fullUrl =
        this.customReportingTimeframe.start && this.customReportingTimeframe.end
          ? `${url}&custom_start=${self.customReportingTimeframe.start}&custom_end=${self.customReportingTimeframe.end}&pipeline_reviewer=${self.pipelineReviewerFilter}`
          : url;

      self.$store.commit('general/showLoadingSpinner');
      fetch(fullUrl)
        .then(response => {
          return response.json();
        })
        .then(data => {
          self.$store.commit('general/hideLoadingSpinner');
          self.candidatesSourced = data.candidates_sourced;
          self.activeAgents = data.active_agents;
          self.hireRate = data.hire_rate;
          self.phoneScreenRate = data.phone_screen_rate;
          self.interviewRate = data.interview_rate;
          self.candidatesPhoneScreened = data.candidates_phone_screened;
          self.candidatesInterviewed = data.candidates_interviewed;
          self.candidatesHired = data.candidates_hired;
          self.candidatesUnlicensed = data.candidates_unlicensed;
          self.candidatesUnlicensedCourseworkStarted = data.candidates_unlicensed_coursework_started;
          self.todaysMeetings = data.todays_meetings;
          self.tomorrowsMeetings = data.tomorrows_meetings;
          self.pipelineReviews = data.pipeline_reviews;
          self.pipelineReviewsCompleted = data.pipeline_reviews_completed;
          self.pipelineReviewCompletionRate = data.pipeline_review_completion_rate;
          self.eligiblePipelineReviewers = data.eligible_pipeline_reviewers;
          self.showCustomDatePicker = false;
        });
    },
    openCustomTimeframeMenu(e) {
      e.stopPropagation();

      const self = this;
      this.metricsTimeline = 'Custom';
      this.showCustomDatePicker = true;

      this.$nextTick(() => {
        if (this.$refs.flatpickrInput) {
          const $fp = flatpickr(this.$refs.flatpickrInput, {
            mode: 'range',
            dateFormat: 'Y-m-d',
            defaultDate: [this.customReportingTimeframe.start, this.customReportingTimeframe.end],
            onValueUpdate: dates => {
              if (dates.length == 2) {
                this.customReportingTimeframe.start = self.formatDate(dates[0], 'yyyy-MM-dd');
                this.customReportingTimeframe.end = self.formatDate(dates[1], 'yyyy-MM-dd');
              } else {
                this.customReportingTimeframe.start = self.formatDate(dates[0], 'yyyy-MM-dd');
                this.customReportingTimeframe.end = null;
              }
            },
          });

          $fp.open();

          document.addEventListener('click', () => {
            this.showCustomDatePicker = false;
          });
        }
      });
    },
    meetingUrl(meeting) {
      if (
        this.$store.state.general.currentUserAccountDetails?.featureFlags.interview_scorecards &&
        meeting.event_type != 'pipeline_review'
      ) {
        return `/contact_events/${meeting.contact_event_id}/scorecard`;
      } else if (
        this.$store.state.general.currentUserAccountDetails?.featureFlags.pipeline_reviews &&
        meeting.event_type == 'pipeline_review'
      ) {
        return `/contact_events/${meeting.contact_event_id}/pipeline_review`;
      } else {
        return `/contacts/${meeting.id}`;
      }
    },
    eventAction(meeting) {
      if (
        this.$store.state.general.currentUserAccountDetails?.featureFlags?.interview_scorecards &&
        meeting.event_type != 'pipeline_review'
      ) {
        return `Script & Scorecard`;
      } else if (
        this.$store.state.general.currentUserAccountDetails?.featureFlags?.pipeline_reviews &&
        meeting.event_type == 'pipeline_review'
      ) {
        return `Pipeline Review`;
      } else {
        return `View Candidate`;
      }
    },
  },
};
</script>

<style>
@reference "tailwindcss";
.flatpickr-day.startRange,
.flatpickr-day.endRange {
  @apply bg-slate-800 border-slate-800;
}
.flatpickr-day.selected,
.flatpickr-day.startRange,
.flatpickr-day.endRange,
.flatpickr-day.selected.inRange,
.flatpickr-day.startRange.inRange,
.flatpickr-day.endRange.inRange,
.flatpickr-day.selected:focus,
.flatpickr-day.startRange:focus,
.flatpickr-day.endRange:focus,
.flatpickr-day.selected:hover,
.flatpickr-day.startRange:hover,
.flatpickr-day.endRange:hover,
.flatpickr-day.selected.prevMonthDay,
.flatpickr-day.startRange.prevMonthDay,
.flatpickr-day.endRange.prevMonthDay,
.flatpickr-day.selected.nextMonthDay,
.flatpickr-day.startRange.nextMonthDay,
.flatpickr-day.endRange.nextMonthDay {
  @apply bg-slate-700 border-slate-700;
}
</style>
